import React, { useState, useEffect } from 'react';

import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';

const CATEGORY_OPTIONS = [
  { value: 'cleaning', label: 'Cleaning Fee' },
  { value: 'service', label: 'Service Fee' },
  { value: 'tax', label: 'Tax' },
  { value: 'discount', label: 'Discount' },
  { value: 'deposit', label: 'Security Deposit' },
  { value: 'extra_guest', label: 'Extra Guest Fee' },
  { value: 'tourist tax adults', label: 'Tourist Tax Adults' },  
  { value: 'tourist tax children', label: 'Tourist Tax Children' },
  { value: 'other', label: 'Other' }
];

const COST_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'variable', label: 'Variable' },
  { value: 'onetime', label: 'One time cost' }
];

const BookingCostsTab = ({ costs, apartmentId, onUpdate }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentCost, setCurrentCost] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 5;
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apartments, setApartments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category: 'other',
    cost_type: 'fixed',
    value: '',
    currency: '',
    apartment: '',
    booking: '',
    start_date: '',
    end_date: '',
    is_active: true
  });

  const getApiBaseUrl = () => process.env.REACT_APP_API_URL || "http://localhost:8000";

  useEffect(() => {
    const fetchApartments = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/properties/apartments/`);
        if (response.ok) setApartments(await response.json());
      } catch (error) {
        console.error('Error fetching apartments:', error);
      }
    };
    fetchApartments();
  }, []);

  const fetchBookings = async (apartmentId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/properties/apartments/${apartmentId}/bookings/`);
      if (response.ok) setBookings(await response.json());
      else setBookings([]);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    }
  };

  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/currencies/`);
        if (response.ok) setCurrencies(await response.json());
      } catch (error) {
        console.error('Error fetching currencies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCurrencies();
  }, []);

  const handleEditClick = (cost) => {
  setCurrentCost(cost);

  const newFormData = {
      name: cost.name || '',
      category: cost.category || 'other',
      cost_type: cost.cost_type || 'fixed',
      value: cost.value ?? '',
      currency_id: cost.currency?.id ?? '',
      apartment_id: cost.apartment?.id ?? '',
      booking: '', // temporarily empty until bookings are fetched
      start_date: cost.start_date ? cost.start_date : '',
      end_date: cost.end_date ? cost.end_date : '',
      is_active: cost.is_active ?? true
    };

    setFormData(newFormData);

    if (cost.booking_info?.apartment_id) {
      fetchBookings(cost.booking_info.apartment_id).then(() => {
        setFormData(prev => ({ ...prev, booking: cost.booking ?? '' }));
      });
    }

    setEditDialogOpen(true);
  };

  const handleAddClick = () => {
    setCurrentCost(null);
    setFormData({
      name: '',
      category: 'other',
      cost_type: 'fixed',
      value: '',
      currency: '',
      apartment: '',
      booking: '',
      start_date: '',
      end_date: '',
      is_active: true
    });
    setBookings([]);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const url = currentCost
        ? `${baseUrl}/api/costs/booking-costs/${currentCost.id}/`
        : `${baseUrl}/api/costs/booking-costs/`;
      const method = currentCost ? 'PATCH' : 'POST';

      // Utility to format date values safely
      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        if (isNaN(d.getTime())) return null;
        return d.toISOString().split("T")[0]; // -> "YYYY-MM-DD"
      };

      const payload = {
        ...formData,
        apartment_id: formData.apartment_id || apartmentId,
        currency_id: formData.currency_id,
        is_booking_related: true,
        start_date: formatDate(formData.start_date),
        end_date: formatDate(formData.end_date),
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setEditDialogOpen(false);
        onUpdate();
      } else {
        const errorText = await response.text();
        console.error('Error saving cost:', errorText);
      }
    } catch (error) {
      console.error('Error saving cost:', error);
    }
  };


  const handleDelete = async (costId) => {
    if (!window.confirm('Are you sure you want to delete this cost?')) return;
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/costs/booking-costs/${costId}/`, { method: 'DELETE' });
      if (response.ok) onUpdate();
      else console.error('Error deleting cost:', await response.text());
    } catch (error) {
      console.error('Error deleting cost:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Booking Costs</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddClick}>Add Cost</Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Validity Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(cost => (
              <TableRow key={cost.id}>
                <TableCell>{cost.name}</TableCell>
                <TableCell>{CATEGORY_OPTIONS.find(opt => opt.value === cost.category)?.label || cost.category}</TableCell>
                <TableCell>{cost.cost_type || ''}</TableCell>
                <TableCell>{cost.value ?? ''} {cost.cost_type === 'percentage' ? '%' : cost.currency?.symbol || ''}</TableCell>
                <TableCell>
                  {cost.start_date && cost.end_date
                    ? `${new Date(cost.start_date).toLocaleDateString()} - ${new Date(cost.end_date).toLocaleDateString()}`
                    : 'Always valid'}
                </TableCell>
                <TableCell>{cost.is_active !== null ? (cost.is_active ? 'Active' : 'Inactive') : ''}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEditClick(cost)}><Edit /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(cost.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={costs.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[5]}
      />

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{currentCost ? 'Edit Cost' : 'Add New Cost'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Cost Name"
              value={formData.name ?? ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category ?? 'other'}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {CATEGORY_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Cost Type</InputLabel>
              <Select
                value={formData.cost_type ?? 'fixed'}
                onChange={(e) => setFormData({ ...formData, cost_type: e.target.value })}
              >
                {COST_TYPE_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              label="Value"
              type="number"
              value={formData.value ?? ''}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              fullWidth
              inputProps={{
                step: '0.01',
                min: formData.cost_type === 'percentage' ? 0 : undefined,
                max: formData.cost_type === 'percentage' ? 100 : undefined
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency_id ?? ''}
                onChange={(e) => setFormData({ ...formData, currency_id: e.target.value })}
                disabled={loading}
              >
                <MenuItem value=""><em>Select Currency</em></MenuItem>
                {currencies.map(c => <MenuItem key={c.id} value={c.id}>{c.symbol} - {c.name} ({c.code})</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Apartment</InputLabel>
              <Select
                value={formData.apartment_id ?? ''}
                onChange={(e) => {
                  const apartmentId = e.target.value;
                  setFormData({ ...formData, apartment_id: apartmentId, booking: '' });
                  fetchBookings(apartmentId);
                }}
              >
                <MenuItem value=""><em>Select Apartment</em></MenuItem>
                {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.apartment_name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!formData.apartment}>
              <InputLabel>Booking</InputLabel>
              <Select
                value={formData.booking ?? ''}
                onChange={(e) => setFormData({ ...formData, booking: e.target.value })}
              >
                <MenuItem value=""><em>Select Booking</em></MenuItem>
                {bookings.map(b => <MenuItem key={b.id} value={b.id}>{b.name || `Booking #${b.id}`}</MenuItem>)}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.start_date ?? ''}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={formData.end_date ?? ''}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.is_active ?? true}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingCostsTab;
